import React from 'react'

import { Box, Flex, Link, SimpleGrid, Text } from '@driftt/dds'

import {
  AccountActivity,
  ActivityType,
  AIScoringTopActivities
} from 'modules/accountActivity/domain'
import { capitalizeStrings } from 'utils/formatStrings'
import { timeSinceUpdated } from 'utils/timeStamp'

const getTitleForActivity = (
  type: ActivityType,
  role: string,
  attributes: Partial<AIScoringTopActivities['attributes']>
) => {
  const { categorizedUrl = '' } = attributes || {}
  const formattedRole = role !== 'other' && role ? capitalizeStrings(role) : 'Site Visitor'

  switch (type) {
    case ActivityType.CONVERSATION:
      return `Conversation with ${formattedRole}`
    case ActivityType.EMAIL_OPEN:
      return `Email opened by ${formattedRole}`
    case ActivityType.MEETING_BOOKED:
      return `Meeting booked with ${formattedRole}`
    case ActivityType.PAGE_VIEW:
      return `${capitalizeStrings(
        categorizedUrl.split('_').join(' ')
      )} page visit from ${formattedRole}`
    case ActivityType.VIDEO:
      return `Video view from ${formattedRole}`
    default:
      return ''
  }
}

const getActivityMetaData = (
  type: ActivityType,
  time: number,
  attributes: Partial<AIScoringTopActivities['attributes']>
) => {
  const activityDate = `${timeSinceUpdated(time)} ago`
  const { pageTitle = '', pageUrl = '', endUserName = '', endUserId } = attributes || {}
  switch (type) {
    case ActivityType.MEETING_BOOKED:
    case ActivityType.CONVERSATION:
      return (
        <>
          <Text as="span" variant="metadata" truncate>
            <Link
              css={{
                textDecoration: 'underline',
                paddingBottom: '$half'
              }}
              href={`/activity/contacts/${endUserId}`}
              type="subtle"
              size="small">
              {endUserName}
            </Link>
          </Text>

          <Text as="span" css={{ flex: 'none' }} variant="metadata">{` • ${activityDate}`}</Text>
        </>
      )
    case ActivityType.PAGE_VIEW:
      return (
        <>
          <Text as="span" variant="metadata" truncate>
            <Link
              css={{
                textDecoration: 'underline',
                paddingBottom: '$half'
              }}
              href={pageUrl}
              type="subtle"
              target="_blank"
              external
              size="small">
              &quot;{capitalizeStrings(pageTitle)}&quot;
            </Link>
          </Text>

          <Text as="span" css={{ flex: 'none' }} variant="metadata">{` • ${activityDate}`}</Text>
        </>
      )
    case ActivityType.VIDEO:
    case ActivityType.EMAIL_OPEN:
      return (
        <Text as="span" variant="metadata">
          {activityDate}
        </Text>
      )
    default:
      return ''
  }
}

const parseActivities = ({
  activity,
  activityId,
  categorizedRole,
  intent,
  receivedAt,
  attributes
}: AIScoringTopActivities) => {
  return {
    id: `${activityId}-${receivedAt}`,
    receivedAt,
    isHighIntent: intent,
    metaData: getActivityMetaData(activity, receivedAt, attributes),
    title: getTitleForActivity(activity, categorizedRole, attributes)
  }
}

const ExplainAIEngagement: React.FC<{
  accountActivity: AccountActivity
}> = ({ accountActivity: { aiScoringTopActivities } }) => {
  if (!aiScoringTopActivities?.length) {
    return null
  }

  const activityEvents = aiScoringTopActivities.map(parseActivities).sort((a, b) => {
    if (a.isHighIntent && !b.isHighIntent) {
      return -1
    }

    if (!a.isHighIntent && b.isHighIntent) {
      return 1
    }

    if (a.isHighIntent && b.isHighIntent) {
      return a.receivedAt > b.receivedAt ? -1 : 1
    }

    if (!a.isHighIntent && !b.isHighIntent) {
      return a.receivedAt > b.receivedAt ? -1 : 1
    }

    return 0
  })

  return (
    <SimpleGrid columns="1">
      {activityEvents.map(({ metaData, id, title, isHighIntent }: any) => (
        <Box
          css={{
            borderBottom: 'solid 1px $gray2',
            overflow: 'hidden',
            padding: '$1 0'
          }}
          key={id}>
          <Flex
            grow={1}
            alignItems="center"
            css={{
              width: '100%;',
              overflow: 'hidden',
              paddingBottom: '$half'
            }}>
            {isHighIntent && (
              <Text
                as="span"
                css={{
                  background: '$purple2',
                  padding: '2px $half',
                  borderRadius: '$1',
                  flex: 'none'
                }}>
                High Intent
              </Text>
            )}
            <Text as="span" truncate>
              {title}
            </Text>
          </Flex>

          <Flex grow={1} css={{ width: '100%;', overflow: 'hidden' }}>
            {metaData}
          </Flex>
        </Box>
      ))}
    </SimpleGrid>
  )
}

export default ExplainAIEngagement
